// pages/Devices/devices.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { CheckCircle2, CirclePause, PackageCheck, RotateCcw, Wrench,  X,
  UserPlus,
  FlaskConical,
  CheckCircle,
  PauseCircle,
  ClipboardCheck
 } from 'lucide-react';
import TrashIcon from '../../components/icons/TrashIcon';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchDevices,
  deleteDevice,
  sendSotaCommand,
  sendCotaCommand,
  sendS3OtaCommand,
  getDeviceConfig,
  selectAllDevices,
  selectDevicesLoading,
  selectDevicesError,
  selectIsAdmin,
  selectUserRole,
  selectTotalDevices,
  selectSuccessMessage,
  selectOtaLoading,
  selectOtaResult,
  selectConfigData,
  selectConfigLoading,
  selectConfigDialogOpen,
  selectSelectedConfigDevice,
  selectConfigSearch,
  clearSuccessMessage,
  clearOtaResult,
  clearConfigData,
  openConfigDialog,
  closeConfigDialog,
  setConfigSearch
} from '../../features/devices/deviceSlice';
import DeviceChecklistModal from './DeviceChecklistModal';
import { deviceAPI } from '../../features/devices/deviceAPI';
import DeviceEdit from './deviceedit';
import DeviceAdd from './deviceadd';
import DeviceQRModal from './deviceqrmodal';
import DeviceOTAModal from './deviceotamodal';
import DeviceConfigModal from './deviceconfigmodal';
import DeviceDashboard from './DeviceDashboard';
import './devices.css';

const Devices = () => {
  const dispatch = useDispatch();
  
  const allDevices = useSelector(selectAllDevices);
  const loading = useSelector(selectDevicesLoading);
  const error = useSelector(selectDevicesError);
  const isAdmin = useSelector(selectIsAdmin);
  const loggedInUser = useSelector((state) => state.auth.user);
  const loggedInDealerId = loggedInUser?.dealerId || loggedInUser?.dealerid || deviceAPI.getDealerId();
  const canAddDevice = isAdmin || Number(loggedInDealerId) === 31278;
  const userRole = useSelector(selectUserRole);
  const totalDevices = useSelector(selectTotalDevices);
  const successMessage = useSelector(selectSuccessMessage);
  const otaLoading = useSelector(selectOtaLoading);
  const otaResult = useSelector(selectOtaResult);
  
  // Config selectors
  const configData = useSelector(selectConfigData);
  const configLoading = useSelector(selectConfigLoading);
  const configDialogOpen = useSelector(selectConfigDialogOpen);
  const selectedConfigDevice = useSelector(selectSelectedConfigDevice);
  const configSearch = useSelector(selectConfigSearch);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [goToPage, setGoToPage] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const [isOTAModalOpen, setIsOTAModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [otaType, setOtaType] = useState('');
  const [dealers, setDealers] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [isDevicePairsOpen, setIsDevicePairsOpen] = useState(false);
  const [devicePairs, setDevicePairs] = useState([]);
  const [devicePairsCbid, setDevicePairsCbid] = useState('');
  const [devicePairsLoading, setDevicePairsLoading] = useState(false);
  const [devicePairsError, setDevicePairsError] = useState('');
  const [isProductionTrackingOpen, setIsProductionTrackingOpen] = useState(false);
  const [productionTrackingDevice, setProductionTrackingDevice] = useState(null);
  const [productionTrackingLoading, setProductionTrackingLoading] = useState(false);
  const [productionTrackingSaving, setProductionTrackingSaving] = useState(false);
  const [productionTrackingError, setProductionTrackingError] = useState('');
  const [isProductionTrackingDetailsOpen, setIsProductionTrackingDetailsOpen] = useState(false);
  const [productionTrackingDetailsDevice, setProductionTrackingDetailsDevice] = useState(null);
  const [productionTrackingDetailsLoading, setProductionTrackingDetailsLoading] = useState(false);
  const [productionTrackingDetailsError, setProductionTrackingDetailsError] = useState('');
  const [productionTrackingDetailsSearch, setProductionTrackingDetailsSearch] = useState('');
  const [productionTrackingRecords, setProductionTrackingRecords] = useState([]);
  const [isTrackingHistoryOpen, setIsTrackingHistoryOpen] = useState(false);
  const [isChecklistOpen, setIsChecklistOpen] = useState(false);
  const [productionTrackingForm, setProductionTrackingForm] = useState({
    status: 'UnderTesting',
    testingPersonName: '',
    observations: ''
  });
  
  // Show success message
  useEffect(() => {
    if (successMessage) {
      showAlert('success', successMessage);
      dispatch(clearSuccessMessage());
    }
  }, [successMessage, dispatch]);
  
  // Handle OTA result
  useEffect(() => {
    if (otaResult) {
      if (otaResult.success) {
        showAlert('success', otaResult.message);
      } else {
        showAlert('error', otaResult.message);
      }
      dispatch(clearOtaResult());
      setIsOTAModalOpen(false);
    }
  }, [otaResult, dispatch]);
  
  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => {
      setAlert({ show: false, type: '', message: '' });
    }, 3000);
  };
  
  // Fetch dealers for dropdown
  const fetchDealers = useCallback(async () => {
    if (isAdmin) {
      try {
        const dealersList = await deviceAPI.getAllDealers();
        setDealers(dealersList);
      } catch (error) {
        console.error("Error fetching dealers:", error);
      }
    }
  }, [isAdmin]);
  
  useEffect(() => {
    dispatch(fetchDevices());
  }, [dispatch]);
  
  useEffect(() => {
    if (isAdmin) {
      fetchDealers();
    }
  }, [isAdmin, fetchDealers]);
  
  // Filter devices
  const filteredDevices = useMemo(() => {
    if (!allDevices.length) return [];
    if (!searchTerm) return allDevices;
    
    const searchLower = searchTerm.toLowerCase();
    return allDevices.filter(device =>
      [device.sno, device.dealer, device.deviceId, device.handsetId, device.status]
        .some(field => field && field.toString().toLowerCase().includes(searchLower))
    );
  }, [allDevices, searchTerm]);
  
  // ============ PAGINATION - FIXED ============
  const totalItems = filteredDevices.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));
  
  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);
  
  // Reset to page 1 when rows per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage]);
  
  // Ensure current page is valid
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
    if (currentPage < 1) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);
  
  const indexOfFirst = (currentPage - 1) * rowsPerPage;
  const indexOfLast = Math.min(indexOfFirst + rowsPerPage, totalItems);
  const currentItems = filteredDevices.slice(indexOfFirst, indexOfLast);
  
  // Calculate displayed range
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endItem = Math.min(currentPage * rowsPerPage, totalItems);
  
  // Pagination handlers
  const handlePageChange = useCallback((page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setGoToPage('');
      // Scroll to top of table
      const tableElement = document.querySelector('.table-container');
      if (tableElement) {
        tableElement.scrollTop = 0;
      }
    }
  }, [totalPages]);
  
  const handleRowsPerPageChange = useCallback((e) => {
    const newRowsPerPage = Number(e.target.value);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
    setGoToPage('');
  }, []);
  
  const handleGoToPage = useCallback((e) => {
    e.preventDefault();
    const pageNum = parseInt(goToPage);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
      setGoToPage('');
    }
  }, [goToPage, totalPages]);
  
  // Generate page numbers for display - FIXED
  const getPageNumbers = useCallback(() => {
    const pages = [];
    const maxVisible = 5;
    const halfVisible = Math.floor(maxVisible / 2);
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - halfVisible);
      let endPage = Math.min(totalPages, currentPage + halfVisible);
      
      // Adjust if we're at the beginning
      if (currentPage - halfVisible < 1) {
        endPage = Math.min(totalPages, endPage + (halfVisible - currentPage + 1));
      }
      
      // Adjust if we're at the end
      if (currentPage + halfVisible > totalPages) {
        startPage = Math.max(1, startPage - (currentPage + halfVisible - totalPages));
      }
      
      // Add first page and ellipsis if needed
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push('...');
        }
      }
      
      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Add last page and ellipsis if needed
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push('...');
        }
        pages.push(totalPages);
      }
    }
    
    return pages;
  }, [currentPage, totalPages]);
  
  // OTA Handlers
  const handleSotaClick = (device) => {
    setSelectedDevice(device);
    setOtaType('sota');
    setIsOTAModalOpen(true);
  };
  
  const handleCotaClick = (device) => {
    setSelectedDevice(device);
    setOtaType('cota');
    setIsOTAModalOpen(true);
  };
  
  const handleS3OtaClick = (device) => {
    setSelectedDevice(device);
    setOtaType('s3ota');
    setIsOTAModalOpen(true);
  };
  
  const confirmOTA = async () => {
    if (!selectedDevice) return;
    
    if (otaType === 'sota') {
      await dispatch(sendSotaCommand({
        cbid: selectedDevice.deviceId,  // Control Board ID
        devId: selectedDevice.handsetId  // Sampler ID
      }));
    } else if (otaType === 'cota') {
      await dispatch(sendCotaCommand({
        cbid: selectedDevice.deviceId,  // Control Board ID
        devId: selectedDevice.handsetId  // Sampler ID
      }));
    } else if (otaType === 's3ota') {
      await dispatch(sendS3OtaCommand({
        cbid: selectedDevice.deviceId,  // Control Board ID
        command: "107"
      }));
    }
  };
  
  // Config Handler
  const handleConfigClick = async (device) => {
    dispatch(openConfigDialog(device));
    await dispatch(getDeviceConfig(device.cbid || device.deviceId));
  };
  
  const handleCloseConfigDialog = () => {
    dispatch(closeConfigDialog());
    dispatch(clearConfigData());
  };
  
  const handleConfigSearchChange = (value) => {
    dispatch(setConfigSearch(value));
  };

  
  const handleEditClick = (device) => {
    setSelectedDevice(device);
    setIsEditModalOpen(true);
  };
  
  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedDevice(null);
    dispatch(fetchDevices());
  };
  
  const handleAddModalClose = () => {
    setIsAddModalOpen(false);
    setTimeout(() => {
      dispatch(fetchDevices());
    }, 500);
  };
  
  const handleQRClick = (device) => {
    setSelectedDevice(device);
    setIsQRModalOpen(true);
  };
  
  const handleQRModalClose = () => {
    setIsQRModalOpen(false);
    setSelectedDevice(null);
  };

  const handleDevicePairsClick = async (device) => {
    const cbid = device.cbid || device.deviceId;
    if (!cbid) return;

    setDevicePairsCbid(cbid);
    setDevicePairs([]);
    setDevicePairsError('');
    setIsDevicePairsOpen(true);
    setDevicePairsLoading(true);

    try {
      const response = await fetch(
        `https://alcobrakeadmin.com/v3/us/cadevices/getdevicepairs?controlid=${encodeURIComponent(cbid)}`
      );
      if (!response.ok) throw new Error('Unable to load device pair history.');

      const data = await response.json();
      setDevicePairs(Array.isArray(data) ? data : (data?.data || []));
    } catch (requestError) {
      setDevicePairsError(requestError.message || 'Unable to load device pair history.');
    } finally {
      setDevicePairsLoading(false);
    }
  };

  const handleProductionTrackingClick = async (device, selectedStatus = '') => {
    const handset = device.handsetId || device.devId;
    const controlid = device.cbid || device.deviceId;
    if (!handset || !controlid) {
      showAlert('error', 'Both handset and control-board IDs are required for production tracking.');
      return;
    }

    setProductionTrackingDevice(device);
    setProductionTrackingForm({ status: selectedStatus || 'UnderTesting', testingPersonName: '', observations: '' });
    setProductionTrackingError('');
    setProductionTrackingLoading(true);
    setIsProductionTrackingOpen(true);

    try {
      const records = await deviceAPI.getProductionTracking(handset, controlid);
      const sortedRecords = [...records].sort((a, b) =>
        new Date(b.created_date || 0).getTime() - new Date(a.created_date || 0).getTime()
      );
      const latestRecord = sortedRecords[0];

      if (latestRecord) {
        setProductionTrackingForm({
          status: selectedStatus || (latestRecord.status === 'Registered' ? 'UnderTesting' : (latestRecord.status || 'UnderTesting')),
          testingPersonName: '',
          observations: ''
        });
      }
    } catch (requestError) {
      setProductionTrackingError(requestError.message || 'Unable to load production tracking.');
    } finally {
      setProductionTrackingLoading(false);
    }
  };

  const closeProductionTracking = () => {
    setIsProductionTrackingOpen(false);
    setProductionTrackingDevice(null);
    setProductionTrackingError('');
  };

  const handleProductionTrackingDetailsClick = async (device) => {
    const handset = device.handsetId || device.devId;
    const controlid = device.cbid || device.deviceId;
    if (!handset || !controlid) return;

    setProductionTrackingDetailsDevice(device);
    setProductionTrackingRecords([]);
    setProductionTrackingDetailsError('');
    setProductionTrackingDetailsLoading(true);
    setIsProductionTrackingDetailsOpen(true);
    try {
      const records = await deviceAPI.getProductionTracking(handset, controlid);
      setProductionTrackingRecords([...records].sort((a, b) =>
        new Date(b.created_date || 0).getTime() - new Date(a.created_date || 0).getTime()
      ));
    } catch (requestError) {
      setProductionTrackingDetailsError(requestError.message || 'Unable to load production tracking.');
    } finally {
      setProductionTrackingDetailsLoading(false);
    }
  };

  const closeProductionTrackingDetails = () => {
    setIsProductionTrackingDetailsOpen(false);
    setProductionTrackingDetailsDevice(null);
    setProductionTrackingDetailsError('');
    setProductionTrackingRecords([]);
    setProductionTrackingDetailsSearch('');
  };

  const handleProductionTrackingSubmit = async (event) => {
    event.preventDefault();
    const handset = productionTrackingDevice?.handsetId || productionTrackingDevice?.devId;
    const controlid = productionTrackingDevice?.cbid || productionTrackingDevice?.deviceId;
    if (!handset || !controlid) return;
    if (!productionTrackingForm.testingPersonName.trim() || !productionTrackingForm.observations.trim()) {
      setProductionTrackingError('Testing person name and observations are required.');
      return;
    }

    setProductionTrackingSaving(true);
    setProductionTrackingError('');
    try {
      await deviceAPI.insertProductionTracking({
        handset,
        controlid,
        status: productionTrackingForm.status,
        registedname: productionTrackingForm.testingPersonName.trim(),
        observations: productionTrackingForm.observations.trim()
      });
      showAlert('success', 'Production tracking saved.');
      closeProductionTracking();
      if (isProductionTrackingDetailsOpen && productionTrackingDetailsDevice) {
        await handleProductionTrackingDetailsClick(productionTrackingDetailsDevice);
      }
    } catch (requestError) {
      setProductionTrackingError(requestError.message || 'Unable to save production tracking.');
    } finally {
      setProductionTrackingSaving(false);
    }
  };
  
  const handleDeleteClick = (device) => {
    setDeviceToDelete(device);
    setShowDeleteConfirm(true);
  };
  
  const confirmDelete = async () => {
    if (deviceToDelete) {
      await dispatch(deleteDevice(deviceToDelete.handsetId || deviceToDelete.deviceId));
      setShowDeleteConfirm(false);
      setDeviceToDelete(null);
    }
  };
  
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeviceToDelete(null);
  };

  const formatInitialized = (value) => {
  if (!value) return '—';

  const date = new Date(value.replace(' ', 'T'));

  if (isNaN(date.getTime())) return value;

  const pad = (num) => String(num).padStart(2, '0');

  return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

  const filteredProductionTrackingRecords = useMemo(() => {
    const search = productionTrackingDetailsSearch.trim().toLowerCase();
    if (!search) return productionTrackingRecords;
    return productionTrackingRecords.filter(record => [record.status, record.registedname, record.registeredname, record.observations, record.created_date]
      .some(value => String(value || '').toLowerCase().includes(search)));
  }, [productionTrackingDetailsSearch, productionTrackingRecords]);

const downloadProductionTracking = async () => {
  if (!productionTrackingRecords || productionTrackingRecords.length === 0) {
    alert('No production tracking records available to download.');
    return;
  }

  try {
    const workbook = new ExcelJS.Workbook();

    const worksheet = workbook.addWorksheet(
      'Production Tracking'
    );

    // ------------------------------------------------
    // EXCEL COLUMNS
    // ------------------------------------------------

    worksheet.columns = [
      {
        header: 'Handset',
        key: 'handset',
        width: 25
      },
      {
        header: 'Control ID',
        key: 'controlid',
        width: 30
      },
      {
        header: 'Status',
        key: 'status',
        width: 20
      },
      {
        header: 'Testing Person',
        key: 'testingPerson',
        width: 25
      },
      {
        header: 'Observations',
        key: 'observations',
        width: 40
      },
      {
        header: 'Created Time',
        key: 'created_date',
        width: 25
      }
    ];

    // ------------------------------------------------
    // HEADER STYLE
    // ------------------------------------------------

    const headerRow = worksheet.getRow(1);

    headerRow.height = 28;

    headerRow.eachCell((cell) => {
      cell.font = {
        bold: true,
        color: {
          argb: 'FFFFFF'
        }
      };

      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: {
          argb: '2563EB'
        }
      };

      cell.alignment = {
        horizontal: 'center',
        vertical: 'middle'
      };

      cell.border = {
        top: {
          style: 'thin',
          color: {
            argb: 'D1D5DB'
          }
        },
        bottom: {
          style: 'thin',
          color: {
            argb: 'D1D5DB'
          }
        },
        left: {
          style: 'thin',
          color: {
            argb: 'D1D5DB'
          }
        },
        right: {
          style: 'thin',
          color: {
            argb: 'D1D5DB'
          }
        }
      };
    });

    // ------------------------------------------------
    // IMPORTANT:
    // USE productionTrackingRecords
    // NOT filteredProductionTrackingRecords
    // ------------------------------------------------

    productionTrackingRecords.forEach((record) => {
      const row = worksheet.addRow({
        handset:
          productionTrackingDetailsDevice?.handsetId ||
          productionTrackingDetailsDevice?.devId ||
          '—',

        controlid:
          productionTrackingDetailsDevice?.cbid ||
          productionTrackingDetailsDevice?.deviceId ||
          '—',

        status:
          record.status || 'Unknown',

        testingPerson:
          record.registedname ||
          record.registeredname ||
          record.comment ||
          '—',

        observations:
          record.observations || '—',

        created_date:
          record.created_date
            ? formatInitialized(record.created_date)
            : '—'
      });

      // ------------------------------------------------
      // GENERAL CELL STYLE
      // ------------------------------------------------

      row.eachCell((cell) => {
        cell.alignment = {
          vertical: 'middle',
          wrapText: true
        };

        cell.border = {
          top: {
            style: 'thin',
            color: {
              argb: 'E5E7EB'
            }
          },
          bottom: {
            style: 'thin',
            color: {
              argb: 'E5E7EB'
            }
          },
          left: {
            style: 'thin',
            color: {
              argb: 'E5E7EB'
            }
          },
          right: {
            style: 'thin',
            color: {
              argb: 'E5E7EB'
            }
          }
        };
      });

      // ------------------------------------------------
      // STATUS COLOR CODING
      // ------------------------------------------------

      const statusCell = row.getCell('status');

      const status =
        String(record.status || '').trim();

      let backgroundColor = 'E5E7EB';
      let textColor = '374151';

      // Registered = RED
      if (status === 'Registered') {
        backgroundColor = 'FECACA';
        textColor = 'B91C1C';
      }

      // Under Testing = ORANGE
      else if (status === 'UnderTesting') {
        backgroundColor = 'FED7AA';
        textColor = 'C2410C';
      }

      // Test Completed = GREEN
      else if (status === 'Testcompleted') {
        backgroundColor = 'BBF7D0';
        textColor = '15803D';
      }

      // Installed = BLUE
      else if (status === 'Installed') {
        backgroundColor = 'BFDBFE';
        textColor = '1D4ED8';
      }

      // Removed = GRAY
      else if (status === 'Removed') {
        backgroundColor = 'E5E7EB';
        textColor = '374151';
      }

      // Not Assigned = YELLOW
      else if (status === 'NotAssigned') {
        backgroundColor = 'FEF3C7';
        textColor = '92400E';
      }

      // Trashed = DARK RED
      else if (status === 'Trashed') {
        backgroundColor = 'FECACA';
        textColor = '991B1B';
      }

      statusCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: {
          argb: backgroundColor
        }
      };

      statusCell.font = {
        bold: true,
        color: {
          argb: textColor
        }
      };

      statusCell.alignment = {
        horizontal: 'center',
        vertical: 'middle'
      };
    });

    // ------------------------------------------------
    // FREEZE HEADER
    // ------------------------------------------------

    worksheet.views = [
      {
        state: 'frozen',
        ySplit: 1
      }
    ];

    // ------------------------------------------------
    // ENABLE EXCEL FILTER
    // ------------------------------------------------

    worksheet.autoFilter = {
      from: 'A1',
      to: 'F1'
    };

    // ------------------------------------------------
    // FILE NAME
    // ------------------------------------------------

    const handset =
      productionTrackingDetailsDevice?.handsetId ||
      productionTrackingDetailsDevice?.devId ||
      'Device';

    const safeHandset =
      String(handset).replace(
        /[^a-zA-Z0-9_-]/g,
        '_'
      );

    const fileName =
      `Production_Tracking_${safeHandset}.xlsx`;

    // ------------------------------------------------
    // CREATE DOWNLOAD
    // ------------------------------------------------

    const buffer =
      await workbook.xlsx.writeBuffer();

    const blob = new Blob(
      [buffer],
      {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    );

    const url =
      window.URL.createObjectURL(blob);

    const link =
      document.createElement('a');

    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);

  } catch (error) {
    console.error(
      'Production tracking Excel download failed:',
      error
    );

    alert(
      'Unable to download production tracking records.'
    );
  }
};

  const formatDuration = (duration) => {
  if (!duration) return "-";

  return duration
    .replace(/\s*hours?\s*/i, "h ")
    .replace(/\s*minutes?\s*/i, "m ")
    .replace(/\s*seconds?\s*/i, "s ")
    .replace(/\s+/g, " ")
    .trim();
};

  const getStatusClass = (status) => {
    switch(status) {
      case 'Installed': return 'status-installed';
      case 'Removed': return 'status-removed';
      case 'NotAssigned': return 'status-notassigned';
      default: return '';
    }
  };
  
const renderDevicesSkeleton = () => {
  const skeletonRows = Array.from({ length: 8 });
 
  return (
    <div className="devices-app">
      <div className="devices-card">
 
        <div className="devices-header">
          <div>
            <h1 className="devices-title">Devices</h1>
            <p className="devices-subtitle">
              Manage and monitor all connected devices
            </p>
          </div>
 
          <div className="devices-header-actions">
            <div className="stat-pill">
              <span className="stat-dot" />
              <span className="stat-label">Records</span>
              <div
                className="device-skeleton"
                style={{ width: 28, height: 14 }}
              />
            </div>
 
            {canAddDevice && (
              <div className="device-skeleton device-skeleton-add-btn" />
            )}
          </div>
        </div>
 
        {/* FILTERS */}
        <div className="filters-bar">
          <div className="device-skeleton device-skeleton-search" />
 
          <div className="device-skeleton device-skeleton-show" />
        </div>
 
        {/* TABLE */}
        <div className="table-container">
          <table className="devices-table">
 
            <thead>
              <tr>
                <th>SNO</th>
                <th>Dealer</th>
                <th>Device ID</th>
                <th>Hand Set ID</th>
                <th className="text-center">Device Pairs</th>
                <th className="text-center">Device Track</th>
                <th className="text-center">Testing Duration</th>
                <th className="text-center">Status</th>
                <th>Ownership Type</th>
 
                {isAdmin && (
                  <>
                    <th>Time Zone</th>
                    <th className="text-center">COTA</th>
                    <th className="text-center">SOTA</th>
                    <th className="text-center">S3OTA</th>
                  </>
                )}
 
                <th className="text-center">Edit</th>
 
                {isAdmin && (
                  <th className="text-center">Delete</th>
                )}
 
                <th className="text-center">QR Code</th>
              </tr>
            </thead>
 
            <tbody>
              {skeletonRows.map((_, index) => (
                <tr key={index}>
 
                  <td>
                    <div className="device-skeleton device-skeleton-sno" />
                  </td>
 
                  <td>
                    <div className="device-skeleton device-skeleton-dealer" />
                  </td>
 
                  <td>
                    <div className="device-skeleton device-skeleton-device-id" />
                  </td>
 
                  <td>
                    <div className="device-skeleton device-skeleton-handset-id" />
                  </td>
 
                  <td>
                    <div className="device-skeleton device-skeleton-history-btn" />
                  </td>
 
                  <td>
                    <div className="device-skeleton device-skeleton-details-btn" />
                  </td>
 
                  <td>
                    <div className="device-skeleton device-skeleton-duration" />
                  </td>
 
                  <td>
                    <div className="device-skeleton device-skeleton-status" />
                  </td>
                  <td>
                    <div className="device-skeleton device-skeleton-status" />
                  </td>
 
                  {isAdmin && (
                    <>
                      <td>
                        <div className="device-skeleton device-skeleton-timezone" />
                      </td>
 
                      <td>
                        <div className="device-skeleton device-skeleton-icon-btn" />
                      </td>
 
                      <td>
                        <div className="device-skeleton device-skeleton-icon-btn" />
                      </td>
 
                      <td>
                        <div className="device-skeleton device-skeleton-icon-btn" />
                      </td>
                    </>
                  )}
                   <td><div className="device-skeleton device-skeleton-icon-btn" /></td>

                  <td>
                    <div className="device-skeleton device-skeleton-icon-btn" />
                  </td>
 
                  {isAdmin && (
                    <td>
                      <div className="device-skeleton device-skeleton-icon-btn" />
                    </td>
                  )}
 
                  <td>
                    <div className="device-skeleton device-skeleton-icon-btn" />
                  </td>
 
                </tr>
              ))}
            </tbody>
 
          </table>
        </div>
 
      </div>
    </div>
  );
};
 
 


  if (loading && allDevices.length === 0) {

    return renderDevicesSkeleton();
    // return (
    //   <div className="devices-app">
    //     <div className="devices-card">
    //       <div className="loading-container">
    //         <div className="loading-spinner"></div>
    //         <p>Loading devices...</p>
    //       </div>
    //     </div>
    //   </div>
    // );
  }
  
  if (error) {
    return (
      <div className="devices-app">
        <div className="devices-card">
          <div className="error-container">
            <p className="error-message">{error}</p>
            <button className="retry-btn" onClick={() => dispatch(fetchDevices())}>Retry</button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="devices-app">
      {/* Alert Banner */}
      {alert.show && createPortal(
        <div className={`alert-banner devices-alert-banner ${alert.type}`} role="status" aria-live="polite">
          <span>{alert.message}</span>
          <button className="alert-close" onClick={() => setAlert({ show: false })}>✕</button>
        </div>,
        document.body
      )}

      {isDevicePairsOpen && (
        <div className="modal-overlay" onClick={() => setIsDevicePairsOpen(false)}>
          <div className="device-pairs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="device-pairs-modal-header">
              <div>
                <h3>Device Pair History</h3>
                <span>CBID: {devicePairsCbid}</span>
              </div>
              <button type="button" onClick={() => setIsDevicePairsOpen(false)} aria-label="Close">×</button>
            </div>
            <div className="device-pairs-modal-body">
              {devicePairsLoading ? (
                <p>Loading history…</p>
              ) : devicePairsError ? (
                <p className="device-pairs-error">{devicePairsError}</p>
              ) : devicePairs.length === 0 ? (
                <p>No pair history found.</p>
              ) : (
                <div className="device-pairs-table-wrap">
                  <table className="device-pairs-table">
                    <thead>
                      <tr><th>Sampler ID</th><th>Control ID</th><th>Time(GMT)</th></tr>
                    </thead>
                    <tbody>
                      {devicePairs.map((pair) => (
                        <tr key={pair.id || `${pair.samplerid}-${pair.inittime}`}>
                          <td>{pair.samplerid || '—'}</td>
                          <td>{pair.controlid || devicePairsCbid}</td>
                          <td>{formatInitialized(pair.inittime)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="device-pairs-modal-footer">
              <button type="button" onClick={() => setIsDevicePairsOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {isProductionTrackingOpen && productionTrackingDevice && (
        <div className="modal-overlay production-tracking-form-overlay" onClick={closeProductionTracking}>
          <form className="production-tracking-modal production-tracking-form-modal" onClick={(event) => event.stopPropagation()} onSubmit={handleProductionTrackingSubmit}>
            <div className="device-pairs-modal-header">
              <div>
                <h3>Device Tracking</h3>
                <span>Handset: {productionTrackingDevice.handsetId || productionTrackingDevice.devId} · Control ID: {productionTrackingDevice.cbid || productionTrackingDevice.deviceId}</span>
              </div>
              <button type="button" onClick={closeProductionTracking} aria-label="Close">×</button>
            </div>
            <div className="production-tracking-body">
              {productionTrackingLoading ? (
                <p>Loading tracking details…</p>
              ) : (
                <>
                  {productionTrackingError && <p className="device-pairs-error">{productionTrackingError}</p>}
                  <label>
                    Status
                    <select
                      value={productionTrackingForm.status}
                      onChange={(event) => setProductionTrackingForm(form => ({ ...form, status: event.target.value }))}
                      required
                    >
                      <option value="UnderTesting">Under Testing</option>
                      <option value="Testcompleted">Test Completed</option>
                      <option value="Stopped">Stop / Pause Testing</option>
                      <option value="ContinueTesting">Continue Testing</option>
                      <option value="Observations">Observations Update</option>
                      <option value="ChecklistNotMatched">Checklist Not Matched</option>
                      <option value="readytoinstall">Ready to install</option>
                      <option value="Installed">Installed</option>
                      <option value="Removed">Removed</option>
                      <option value="NotAssigned">NotAssigned</option>
                      <option value="Trashed">Trashed</option>
                    </select>
                  </label>
                  <label>
                    Testing Person Name
                    <input
                      type="text"
                      value={productionTrackingForm.testingPersonName}
                      onChange={(event) => setProductionTrackingForm(form => ({ ...form, testingPersonName: event.target.value }))}
                      placeholder="Enter testing person name"
                      required
                    />
                  </label>
                  <label>
                    Observations
                    <textarea value={productionTrackingForm.observations} onChange={(event) => setProductionTrackingForm(form => ({ ...form, observations: event.target.value }))} rows="2" required />
                  </label>
                  <section className="production-tracking-history" aria-label="Tracking history">
                    <div className="production-tracking-history-heading">
                      <div>
                        <span className="production-history-eyebrow">Tracking history</span>
                        <h4>All production updates</h4>
                      </div>
                      <span className="production-history-count">{productionTrackingRecords.length} records</span>
                    </div>
                    {productionTrackingRecords.length === 0 ? (
                      <p className="production-history-empty">No production tracking records yet.</p>
                    ) : (
                      <div className="production-history-list">
                        {productionTrackingRecords.map((record) => (
                          <article className="production-history-item" key={record.id || `${record.created_date}-${record.status}`}>
                            <div className="production-history-item-top">
                              <span className={`production-status production-status-${String(record.status || '').toLowerCase()}`}>
                                {record.status || 'Unknown'}
                              </span>
                              <time dateTime={record.created_date || undefined}>{formatInitialized(record.created_date)}</time>
                            </div>
                            <dl>
                              <div>
                                <dt>Testing person</dt>
                                <dd>{record.registedname || record.registeredname || record.comment || '—'}</dd>
                              </div>
                              <div>
                                <dt>Observations</dt>
                                <dd>{record.observations || '—'}</dd>
                              </div>
                            </dl>
                          </article>
                        ))}
                      </div>
                    )}
                  </section>
                </>
              )}
            </div>
            <div className="device-pairs-modal-footer">
              <button type="button" className="production-cancel-btn" onClick={closeProductionTracking}>Cancel</button>
              <button type="submit" className="production-save-btn" disabled={productionTrackingLoading || productionTrackingSaving}>
                {productionTrackingSaving ? 'Saving…' : 'Save Tracking'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* {isProductionTrackingDetailsOpen && productionTrackingDetailsDevice && (
        <div className="modal-overlay" onClick={closeProductionTrackingDetails}>
          <section className="production-tracking-modal" onClick={(event) => event.stopPropagation()}>
            <div className="device-pairs-modal-header">
              <div><h3>Production Tracking Details</h3><span>Handset: {productionTrackingDetailsDevice.handsetId || productionTrackingDetailsDevice.devId} · Control ID: {productionTrackingDetailsDevice.cbid || productionTrackingDetailsDevice.deviceId}</span></div>
              <button type="button" onClick={closeProductionTrackingDetails} aria-label="Close">×</button>
            </div>
            <div className="production-tracking-body">
              <section className="production-tracking-history">
                <div className="production-tracking-history-heading"><div><span className="production-history-eyebrow">Tracking history</span><h4>All production updates</h4></div><span className="production-history-count">{filteredProductionTrackingRecords.length} records</span></div>
                <div className="production-details-toolbar">
                  <input value={productionTrackingDetailsSearch} onChange={(event) => setProductionTrackingDetailsSearch(event.target.value)} placeholder="Search status, person, observations, time…" aria-label="Search tracking details" />
                  <button type="button" className="production-export-btn" onClick={downloadProductionTracking} disabled={productionTrackingRecords.length === 0}>Download</button>
                </div>
                {productionTrackingDetailsLoading ? <p className="production-history-empty">Loading tracking details…</p> : productionTrackingDetailsError ? <p className="device-pairs-error">{productionTrackingDetailsError}</p> : productionTrackingRecords.length === 0 ? <p className="production-history-empty">No production tracking records yet.</p> : (
                  <div className="production-history-list">
                    {filteredProductionTrackingRecords.map((record) => (
                      <article className={`production-history-item production-history-${String(record.status || '').toLowerCase()}`} key={record.id || `${record.created_date}-${record.status}`}>
                        <div className="production-history-item-top"><span className={`production-status production-status-${String(record.status || '').toLowerCase()}`}>{record.status || 'Unknown'}</span><time>{formatInitialized(record.created_date)}</time></div>
                        <dl><div><dt>Testing person</dt><dd>{record.registedname || record.registeredname || record.comment || '—'}</dd></div><div><dt>Observations</dt><dd>{record.observations || '—'}</dd></div></dl>
                      </article>
                    ))}
                  </div>
                )}
              </section>
            </div>
            <div className="device-pairs-modal-footer"><button type="button" onClick={closeProductionTrackingDetails}>Close</button></div>
          </section>
        </div>
      )} */}

{isProductionTrackingDetailsOpen && productionTrackingDetailsDevice && (
  <div className="modal-overlay" onClick={closeProductionTrackingDetails}>
    <section
      className="production-tracking-modal production-details-reference"
      onClick={(event) => event.stopPropagation()}
    >
      {/* Header */}
      <div className="device-pairs-modal-header">
        <div>
          <h3>Interlock Device Production Tracking</h3>
          <span>Device Production Status</span>
        </div>

        <button
          type="button"
          onClick={closeProductionTrackingDetails}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <div className="production-tracking-body">

        {productionTrackingDetailsLoading ? (
          <p className="production-history-empty">
            Loading tracking details…
          </p>
        ) : productionTrackingDetailsError ? (
          <p className="device-pairs-error">
            {productionTrackingDetailsError}
          </p>
        ) : productionTrackingRecords.length === 0 ? (
          <div className="production-history-empty">
            No production tracking records yet.
            <button type="button" className="production-history-view-btn" onClick={() => handleProductionTrackingClick(productionTrackingDetailsDevice)}>
              Track
            </button>
          </div>
        ) : (
          (() => {
            const records = productionTrackingRecords;

            const normalizeStatus = (status) => {
              if (!status) return '';

              const value = String(status)
                .toLowerCase()
                .replace(/[\s_-]/g, '');

              const mapping = {
                registered: 'Registered',
                undertesting: 'UnderTesting',
                testingcompleted: 'TestingCompleted',
                testcompleted: 'TestingCompleted',
                readytoinstall: 'ReadyToInstall',
                // stopped: 'UnderTesting',
                // paused: 'UnderTesting',
                stopped: 'Stopped',
paused: 'Paused',
                issueraised: 'UnderTesting',
                continuetesting: 'UnderTesting',
                observations: 'UnderTesting',
                checklistnotmatched: 'UnderTesting'
              };

              return mapping[value] || status;
            };

            const stages = [
              {
                key: 'Registered',
                label: 'Registered',
                icon: CheckCircle2
              },
              {
                key: 'UnderTesting',
                label: 'Under Testing',
                icon: Wrench
              },
              {
                key: 'TestingCompleted',
                label: 'Testing Completed',
                icon: CheckCircle2
              },
              {
                key: 'ReadyToInstall',
                label: 'Ready To Install',
                icon: PackageCheck
              }
            ];

            const latestRecord = records[0];
            const currentStatus = normalizeStatus(latestRecord?.status);
            const currentIndex = stages.findIndex(
              (stage) => stage.key === currentStatus
            );

            const getStageRecord = (stage) =>
              records.find(
                (record) =>
                  normalizeStatus(record.status) === stage.key
              );

            const openTestingAction = (status) => {
              handleProductionTrackingClick(productionTrackingDetailsDevice, status);
            };

           return (
  <>
    {/* Back to Under Testing */}
    {(currentStatus === 'TestingCompleted' ||
      currentStatus === 'ReadyToInstall') && (
      <div className="reference-back-testing">
        <button
          type="button"
          onClick={() => openTestingAction('UnderTesting')}
        >
          ← Back to Under Testing
        </button>
      </div>
    )}

    {/* Device Information */}
    <div className="reference-device-info">
      <div className="reference-device-box">

        <div className="reference-device-item">
          <div className="reference-device-label">
            Handset
          </div>

          <div className="reference-device-value">
            {productionTrackingDetailsDevice.handsetId ||
              productionTrackingDetailsDevice.devId ||
              '—'}
          </div>
        </div>

        <div className="reference-device-item">
          <div className="reference-device-label">
            Control ID
          </div>

          <div className="reference-device-value">
            {productionTrackingDetailsDevice.cbid ||
              productionTrackingDetailsDevice.deviceId ||
              '—'}
          </div>
        </div>
        <div className="production-device-actions">
          <button
            type="button"
            className="production-history-view-btn"
            onClick={() => setIsTrackingHistoryOpen(true)}
          >
            View History
          </button>
          <button
            type="button"
            className="production-history-view-btn production-device-track-btn"
            onClick={() => handleProductionTrackingClick(productionTrackingDetailsDevice)}
          >
            Track
          </button>
        </div>
  
  

      </div>
    </div>

    {/* Production Flow */}
    <div className="reference-flow">

      {stages.map((stage, index) => {
        const stageRecord = getStageRecord(stage);

        let stageClass = 'reference-stage pending';

        if (currentIndex >= 0 && index < currentIndex) {
          stageClass = 'reference-stage completed';
        } else if (index === currentIndex) {
          stageClass = 'reference-stage current';
        }

        return (
          <React.Fragment key={stage.key}>

            <div className={stageClass}>
              <div className="reference-icon-circle">
                 <stage.icon size={20} />
              </div>

              <div className="reference-stage-name">
                {stage.label}
              </div>

              <div className="reference-stage-date">
                {stageRecord?.created_date
                  ? formatInitialized(stageRecord.created_date)
                  : ''}
              </div>
            </div>

            {index < stages.length - 1 && (
              <div
                className={`reference-connector ${
                  currentIndex > index ? 'completed' : ''
                }`}
              />
            )}

          </React.Fragment>
        );
      })}

    </div>

    {/* Device Testing Actions - ONLY Under Testing */}
  {(currentStatus === 'UnderTesting' ||
  currentStatus === 'Stopped' ||
  currentStatus === 'Paused') && (
      <div className="reference-testing-actions">

        <div className="reference-testing-title">
          Device Testing Actions
        </div>

        <div className="reference-testing-flow">

          {/* Stop / Pause */}
          <button
            type="button"
            className="reference-action-button"
            onClick={() => openTestingAction('Stopped')}
              disabled={currentStatus === 'Stopped' || currentStatus === 'Paused'}

          >
            <div className="reference-action-icon pause">
              ⏸
            </div>

            <span>Stop / Pause</span>
          </button>

          {/* Continue Testing */}
          <button
            type="button"
            className="reference-action-button"
            onClick={() => openTestingAction('ContinueTesting')}
              disabled={currentStatus !== 'Stopped' && currentStatus !== 'Paused'}

          >
            <div className="reference-action-icon continue">
              ▶
            </div>

            <span>Continue Testing</span>
          </button>

          {/* Observations */}
          <button
            type="button"
            className="reference-action-button"
            onClick={() => openTestingAction('Observations')}
          >
            <div className="reference-action-icon observation">
              📝
            </div>

            <span>Observations</span>
          </button>

          {/* Checklist */}
          <button
  type="button"
  className="reference-action-button"
  onClick={() => setIsChecklistOpen(true)}
>
  <div className="reference-action-icon checklist">
    ☑
  </div>

  <span>Checklist</span>
</button>

        </div>
      </div>
    )}

    {/* Current Status */}
    <div className="reference-latest-info">

      <div>
        <strong>Current Status:</strong>{' '}
        {latestRecord?.status || '—'}
      </div>

      <div>
        <strong>Updated By:</strong>{' '}
        {latestRecord?.registedname ||
          latestRecord?.registeredname ||
          latestRecord?.comment ||
          '—'}
      </div>

      <div>
        <strong>Observation:</strong>{' '}
        {latestRecord?.observations ||
          'No observations'}
      </div>

    </div>

    {/* Tracking History */}
    {/* <section className="reference-history">

      <div className="reference-history-header">

        <div>
          <span>Tracking History</span>
          <h4>All Production Updates</h4>
        </div>

        <button
          type="button"
          className="production-export-btn"
          onClick={downloadProductionTracking}
        >
          Download
        </button>

      </div>

      <div className="reference-history-list">

        {filteredProductionTrackingRecords.map((record) => (
          <div
            className="reference-history-item"
            key={
              record.id ||
              `${record.created_date}-${record.status}`
            }
          >

            <div>
              <strong>
                {record.status || 'Unknown'}
              </strong>

              <span>
                {formatInitialized(record.created_date)}
              </span>
            </div>

            <p>
              <b>Testing Person:</b>{' '}
              {record.registedname ||
                record.registeredname ||
                record.comment ||
                '—'}
            </p>

            <p>
              <b>Observation:</b>{' '}
              {record.observations || '—'}
            </p>

          </div>
        ))}

      </div>

       </section> */}

    {isTrackingHistoryOpen && (
  <div
    className="tracking-history-dialog-overlay"
    onMouseDown={() => setIsTrackingHistoryOpen(false)}
  >
    <section
      className="tracking-history-dialog"
      onMouseDown={(event) => event.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tracking-history-dialog-title"
    >

      {/* Dialog Header */}
      <div className="tracking-history-dialog-header">

        <div>
          <span>PRODUCTION TRACKING</span>

          <h3 id="tracking-history-dialog-title">
            Complete Tracking History
          </h3>

          <p>
            Detailed history of all production updates for this device.
          </p>
        </div>

        <div className="tracking-history-dialog-header-actions">

          <button
            type="button"
            className="tracking-history-download-btn"
            onClick={downloadProductionTracking}
          >
            Download
          </button>

          <button
            type="button"
            className="tracking-history-close"
            onClick={() => setIsTrackingHistoryOpen(false)}
            aria-label="Close tracking history"
          >
            <X size={20} />
          </button>

        </div>

      </div>

      {/* History Records */}
      <div className="tracking-history-dialog-body">

        {filteredProductionTrackingRecords.length > 0 ? (

          filteredProductionTrackingRecords.map((record, index) => {

            const status = record.status || 'Unknown';

            const normalizedStatus = status
              .toLowerCase()
              .replace(/\s+/g, '');

            let statusClass = 'status-default';

            if (normalizedStatus === 'registered') {
              statusClass = 'status-registered';
            } else if (normalizedStatus === 'undertesting') {
              statusClass = 'status-testing';
            } else if (normalizedStatus === 'testingcompleted') {
              statusClass = 'status-completed';
            } else if (normalizedStatus === 'readytoinstall') {
              statusClass = 'status-ready';
            } else if (
              normalizedStatus === 'stopped' ||
              normalizedStatus === 'paused'
            ) {
              statusClass = 'status-stopped';
            } else if (normalizedStatus === 'initialtest') {
              statusClass = 'status-initial';
            }

            /* ADD THIS HERE */
let StatusIcon = ClipboardCheck;

if (normalizedStatus === 'registered') {
  StatusIcon = UserPlus;
} else if (normalizedStatus === 'undertesting') {
  StatusIcon = FlaskConical;
} else if (normalizedStatus === 'testingcompleted') {
  StatusIcon = CheckCircle;
} else if (normalizedStatus === 'readytoinstall') {
  StatusIcon = Wrench;
} else if (
  normalizedStatus === 'stopped' ||
  normalizedStatus === 'paused'
) {
  StatusIcon = PauseCircle;
} else if (normalizedStatus === 'initialtest') {
  StatusIcon = ClipboardCheck;
}

            const testingPerson =
              record.registedname ||
              record.registeredname ||
              record.comment ||
              '—';

            const observation =
              record.observations ||
              'No observations';

            return (
              <div
                className={`tracking-history-record ${statusClass}`}
                key={
                  record.id ||
                  `${record.created_date}-${record.status}-${index}`
                }
              >

                <div className="tracking-history-record-main">

                  {/* Status */}
                  <div className="tracking-history-status">
                    <span className="tracking-history-status-icon">
  <StatusIcon size={17} strokeWidth={2.2} />
</span>
  <span>{status}</span>
                  </div>

                  {/* Testing Person */}
                  <div className="tracking-history-person">
                    <span>Testing Person</span>

                    <strong>
                      {testingPerson}
                    </strong>
                  </div>

                  {/* Time */}
                  <div className="tracking-history-time">
                    <span>Time</span>

                    <strong>
                      {formatInitialized(record.created_date)}
                    </strong>
                  </div>

                  {/* Observation */}
                  <div className="tracking-history-observation">
                    <span>Observation</span>

                    <p>
                      {observation}
                    </p>
                  </div>

                </div>

              </div>
            );
          })

        ) : (

          <div className="tracking-history-empty">
            No production tracking history available.
          </div>

        )}

      </div>

      {/* Dialog Footer */}
      <div className="tracking-history-dialog-footer">

        <span>
          Showing{' '}
          <strong>
            {filteredProductionTrackingRecords.length}
          </strong>{' '}
          tracking records
        </span>

        <button
          type="button"
          className="tracking-history-footer-close"
          onClick={() => setIsTrackingHistoryOpen(false)}
        >
          Close
        </button>

      </div>

    </section>
  </div>
)}
  </>
);
          })()
        )}

      </div>

      <div className="device-pairs-modal-footer">
        <button
          type="button"
          onClick={closeProductionTrackingDetails}
        >
          Close
        </button>
      </div>
    </section>
  </div>
)}


  {/* Checklist Modal */}
    <DeviceChecklistModal
      isOpen={isChecklistOpen}
      onClose={() => setIsChecklistOpen(false)}
      controlId={
        productionTrackingDetailsDevice?.cbid ||
        productionTrackingDetailsDevice?.deviceId ||
        productionTrackingDetailsDevice?.controlid ||
        ''
      }
      samplerId={
        productionTrackingDetailsDevice?.handsetId ||
        productionTrackingDetailsDevice?.devId ||
        productionTrackingDetailsDevice?.devid ||
        productionTrackingDetailsDevice?.samplerid ||
        ''
      }
    />

      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deviceToDelete && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-header">
              <h3>Are You Sure?</h3>
            </div>
            <div className="delete-modal-body">
              <p>You want to delete this device - <strong>{deviceToDelete.handsetId || deviceToDelete.deviceId}</strong></p>
              <p>You can't revert your action!</p>
            </div>
            <div className="delete-modal-footer">
              <button className="delete-confirm-btn" onClick={confirmDelete}>Yes, Delete it!</button>
              <button className="delete-cancel-btn" onClick={cancelDelete}>No, Keep it!</button>
            </div>
          </div>
        </div>
      )}
      
      {/* OTA Confirmation Modal */}
      <DeviceOTAModal
        isOpen={isOTAModalOpen}
        onClose={() => setIsOTAModalOpen(false)}
        onConfirm={confirmOTA}
        device={selectedDevice}
        type={otaType}
        loading={otaLoading}
      />
      
      {/* Config Modal */}
      <DeviceConfigModal
        isOpen={configDialogOpen}
        onClose={handleCloseConfigDialog}
        device={selectedConfigDevice}
        configData={configData}
        loading={configLoading}
        searchTerm={configSearch}
        onSearchChange={handleConfigSearchChange}
      />
      
      <div className="devices-card">
        <div className="devices-header">
          <div>
            <h1 className="devices-title">Devices</h1>
            <p className="devices-subtitle">Manage and monitor all connected devices</p>
            <div className="stats-container">
              {searchTerm && <span className="filter-badge">Filtered: {filteredDevices.length}</span>}
            </div>
          </div>
          <div className="devices-header-actions">
            <div className="stat-pill">
              <span className="stat-dot" />
              <span className="stat-label">Records</span>
              <span className="stat-value">{filteredDevices.length}</span>
            </div>
            {canAddDevice && (
              <button className="add-device-btn" onClick={() => setIsAddModalOpen(true)}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 3v12M3 9h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                Add Device
              </button>
            )}
          </div>
        </div>
        
        <div className="filters-bar">
          <div className="search-wrapper">
            <svg className="search-svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M12.5 12.5L16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              className="search-input"
              placeholder="Search by SNO, Device ID, Handset ID, Dealer, Status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && <button className="clear-btn" onClick={() => setSearchTerm('')}>×</button>}
          </div>
          <div className="records-info">
            <span>Show</span>
            <select 
              value={rowsPerPage} 
              onChange={handleRowsPerPageChange}
            >
              {[10, 25, 50, 100].map(n => <option key={n}>{n}</option>)}
            </select>
          </div>
        </div>
        
        {filteredDevices.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📱</div>
            <p>No devices found</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="devices-table">
                <thead>
                  <tr>
                    <th>SNO</th>
                    <th>Dealer</th>
                    <th>Device ID</th>
                    <th>Hand Set ID</th>
                    <th className="text-center">Device Pairs</th>
                    <th className="text-center">Device Track</th>
                    <th className="text-center">Testing Duration</th>
                    <th className="text-center">Status</th>
                    <th>Ownership Type</th>
                    {isAdmin && (
                      <>
                        <th>Time Zone</th>
                        <th className="text-center">COTA</th>
                        <th className="text-center">SOTA</th>
                        <th className="text-center">S3OTA</th>
                      </>
                    )}
                    <th className="text-center">Edit</th>
                    {isAdmin && <th className="text-center">Delete</th>}
                    <th className="text-center">QR Code</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((device) => (
                    <tr key={device.sno}>
                      <td className="sno-cell">{device.sno}</td>
                      <td className="dealer-cell">{device.dealer}</td>
                      <td className="device-id-cell">{device.deviceId}</td>
                      <td className="handset-cell">{device.handsetId}</td>
                      <td className="action-cell text-center">
                        <button
                          type="button"
                          className="device-pairs-btn"
                          onClick={() => handleDevicePairsClick(device)}
                          disabled={!(device.cbid || device.deviceId)}
                        >
                          History
                        </button>
                      </td>
                      <td className="action-cell text-center">
                        <div className="production-tracking-actions">
                          <button type="button" className="device-pairs-btn production-details-btn" onClick={() => handleProductionTrackingDetailsClick(device)} disabled={!(device.handsetId || device.devId) || !(device.cbid || device.deviceId)}>Details</button>
                        </div>
                      </td>
                      <td className="testing-duration-cell">{formatDuration(device.testingduration) || "-"}</td>
                      <td className="status-cell">
                        <span className={`status-badge ${getStatusClass(device.status)}`}>
                          {device.status}
                        </span>
                      </td>
                      <td className="ownership-cell">
                        {device.cam4 ? (
                          <span className={`ownership-badge ownership-${String(device.cam4).trim().toLowerCase()}`}>
                            {device.cam4}
                          </span>
                        ) : '—'}
                      </td>
                      {isAdmin && (
                        <>
                          <td className="timezone-cell">{device.timezone}</td>
                          {/* COTA Button */}
                          <td className="action-cell text-center text-center">
                            <button 
                              className="ota-icon-btn cota" 
                              onClick={() => handleCotaClick(device)} 
                              title="Control Board OTA"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="2" y="2" width="20" height="20" rx="2" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            </button>
                          </td>
                          {/* SOTA Button */}
                          <td className="action-cell text-center text-center">
                            <button 
                              className="ota-icon-btn sota" 
                              onClick={() => handleSotaClick(device)} 
                              title="Sampler OTA"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                              </svg>
                            </button>
                          </td>
                          {/* S3OTA Button */}
                          <td className="action-cell text-center text-center">
                            <button 
                              className="ota-icon-btn s3ota" 
                              onClick={() => handleS3OtaClick(device)} 
                              title="S3 OTA"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 12a9 9 0 0 1-9 9m9-9a9 9 0 0 0-9-9m9 9h-4m-7 9A9 9 0 0 1 3 12m9 9v-4M3 12a9 9 0 0 1 9-9m-9 9h4m7-9a9 9 0 0 1 9 9" />
                              </svg>
                            </button>
                          </td>


                        </>
                      )}
                      <td className="action-cell text-center text-center">
                        <button className="action-icon edit" onClick={() => handleEditClick(device)} title="Edit">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M17 3l4 4-7 7H10v-4l7-7z" />
                            <path d="M4 20l4-4" />
                          </svg>
                        </button>
                      </td>
                      {isAdmin && (
                        <td className="action-cell text-center text-center">
                          <button className="action-icon delete" onClick={() => handleDeleteClick(device)} title="Delete">
                            <TrashIcon />
                          </button>
                        </td>
                      )}
                      <td className="action-cell text-center text-center">
                        <button className="action-icon qr" onClick={() => handleQRClick(device)} title="Generate QR">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="3" width="7" height="7" />
                            <rect x="14" y="3" width="7" height="7" />
                            <rect x="3" y="14" width="7" height="7" />
                            <path d="M14 14h7v7h-7z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* PAGINATION - FIXED */}
            <div className="pagination-container">
              <div className="pagination-info">
                Showing <strong>{startItem}–{endItem}</strong> of <strong>{totalItems}</strong>
              </div>
              <div className="pagination-controls">
                <button 
                  className="page-btn"
                  onClick={() => handlePageChange(1)} 
                  disabled={currentPage === 1 || totalPages === 1}
                >
                  «
                </button>
                <button 
                  className="page-btn"
                  onClick={() => handlePageChange(currentPage - 1)} 
                  disabled={currentPage === 1 || totalPages === 1}
                >
                  ‹
                </button>
                {getPageNumbers().map((p, i) => {
                  if (p === '...') {
                    return <span key={`ellipsis-${i}`} className="page-dots">…</span>;
                  }
                  return (
                    <button 
                      key={`page-${p}`} 
                      className={`page-btn ${currentPage === p ? 'active' : ''}`}
                      onClick={() => handlePageChange(p)}
                    >
                      {p}
                    </button>
                  );
                })}
                <button 
                  className="page-btn"
                  onClick={() => handlePageChange(currentPage + 1)} 
                  disabled={currentPage === totalPages || totalPages === 1}
                >
                  ›
                </button>
                <button 
                  className="page-btn"
                  onClick={() => handlePageChange(totalPages)} 
                  disabled={currentPage === totalPages || totalPages === 1}
                >
                  »
                </button>
              </div>
              <div className="goto-wrapper">
                <form onSubmit={handleGoToPage}>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={goToPage}
                    onChange={e => setGoToPage(e.target.value)}
                    placeholder="Go to"
                    disabled={totalPages === 1}
                  />
                  <button type="submit" disabled={totalPages === 1} style={{color: 'gray'}}>Go</button>
                </form>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Edit Device Modal */}
      <DeviceEdit
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        device={selectedDevice}
        dealers={dealers}
        isAdmin={isAdmin}
      />
      
      {/* Add Device Modal */}
      <DeviceAdd
        isOpen={isAddModalOpen}
        onClose={handleAddModalClose}
        dealers={dealers}
        isAdmin={isAdmin}
      />
      
      {/* QR Code Modal */}
      <DeviceQRModal
        isOpen={isQRModalOpen}
        onClose={handleQRModalClose}
        device={selectedDevice}
      />
    </div>
  );
};

export default Devices;
